import { useNavigate } from "@tanstack/react-router";
import { type FunctionComponent, useState } from "react";
import announceIcon from "@/assets/img/icon_aimoge_alpha.png";
import type { AnnounceBannerItem } from "@/features/announce/api/announceClient";
import { useAnnounceBanners } from "@/features/announce/hooks/useAnnounceBanners";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import { useDesktopAnnouncementModal } from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { activateAnnouncementBanner } from "@/features/announce/lib/announcementBannerTarget";
import {
  dismissAnnouncement,
  isAnnouncementDismissed,
} from "@/features/announce/lib/dismissedAnnouncements";
import {
  selectVisibleBanner,
  shouldShowAnnounceBannerSkeleton,
} from "@/features/announce/lib/selectVisibleBanner";
import { useIsDesktop } from "@/shared/hooks";

const LEVEL_CLASS: Record<AnnounceBannerItem["level"], string> = {
  emergency: "aimg-announce-lv-emergency",
  important: "aimg-announce-lv-important",
  normal: "aimg-announce-lv-normal",
};

const LEVEL_PREFIX: Record<AnnounceBannerItem["level"], string> = {
  emergency: "🚨",
  important: "⭐️",
  normal: "",
};

/**
 * カタログ上部の告知バナー。
 *
 * SPでは取得中に`AnnounceBannerSkeleton`で概算の高さを確保する。
 * PCでは一覧を先に表示し、`DesktopAnnouncementContent`がバナー挿入時の移動を補正する。
 * 取得完了後に表示すべき
 * バナーが無い（0件・全件dismiss済み・取得失敗）場合は空状態を畳む。
 */
export const AnnounceBanner: FunctionComponent = () => {
  const bannersQuery = useAnnounceBanners();
  const [dismissedVersion, setDismissedVersion] = useState(0);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { openAnnouncementModal } = useDesktopAnnouncementModal();
  const { hasUnreadAnnouncements } = useAnnouncementStatus();

  if (bannersQuery.data === undefined) {
    if (isDesktop) return null;
    return shouldShowAnnounceBannerSkeleton(
      bannersQuery.data,
      bannersQuery.isError,
    ) ? (
      <AnnounceBannerSkeleton />
    ) : null;
  }

  void dismissedVersion; // dismiss後の再選出をトリガーするためだけの依存
  const visible = selectVisibleBanner(
    bannersQuery.data,
    isAnnouncementDismissed,
  );
  if (!visible) return null;

  return (
    <AnnounceBannerView
      banner={visible}
      showNewBadge={hasUnreadAnnouncements}
      onOpen={(): void => {
        activateAnnouncementBanner(visible, isDesktop, {
          openDesktop: openAnnouncementModal,
          openMobileDetail: (announcementId: number): void => {
            void navigate({
              to: "/announcements/$announcementId",
              params: { announcementId: String(announcementId) },
            });
          },
        });
      }}
      onDismiss={(): void => {
        dismissAnnouncement(visible.id);
        setDismissedVersion((version) => version + 1);
      }}
    />
  );
};

/**
 * 取得中のプレースホルダ。実際のバナー(`<aside>`)とほぼ同じ外枠
 * (border-b・padding)を持たせ、取得完了時にバナーが表示される場合の
 * 高さの変動を抑える。タイトル1行分の概算高さを確保する。装飾目的のみで
 * 実際の情報を持たないため`aria-hidden`でスクリーンリーダーから隠す。
 */
const AnnounceBannerSkeleton: FunctionComponent = () => (
  <span className="aimg-announce-skeleton" aria-hidden="true" />
);

interface AnnounceBannerViewProps {
  banner: AnnounceBannerItem;
  onDismiss: () => void;
  onOpen: () => void;
  showNewBadge?: boolean;
}

/**
 * 状態を持たない描画専用View。`AnnounceBanner`から分離し、
 * データ取得・dismiss記憶なしでmarkupだけをテストできるようにする。
 */
export const AnnounceBannerView: FunctionComponent<AnnounceBannerViewProps> = ({
  banner,
  onDismiss,
  onOpen,
  showNewBadge = false,
}: AnnounceBannerViewProps) => {
  return (
    <aside aria-label="お知らせ" className="aimg-announce-root">
      <button
        type="button"
        onClick={onOpen}
        className="aimg-announce-link"
        aria-label={`お知らせ: ${banner.title}`}
      >
        <span className="aimg-announce-icon-wrap">
          <img
            className="aimg-announce-icon"
            src={announceIcon}
            alt=""
            width={32}
            height={32}
          />
          {showNewBadge && <strong className="aimg-announce-badge">new</strong>}
        </span>
        <strong
          className={`aimg-announce-title ${LEVEL_CLASS[banner.level]}`}
          aria-live="polite"
        >
          {LEVEL_PREFIX[banner.level]}
          {banner.title}
        </strong>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="お知らせを閉じる"
        className="aimg-announce-close"
      >
        ✕
      </button>
    </aside>
  );
};
